import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchEventSource, EventStreamContentType } from '@microsoft/fetch-event-source';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import useAuthStore from 'store/useAuthStore';
import { LOGIN } from 'constants/routes';
import { catchErrorMessage, getBrowserInfo } from 'utility/helpers';
import { API_CLIENT_ID } from '@buildwise/libs/services';
import useUserData from 'hooks/useUserData';

class RetriableError extends Error {}
class FatalError extends Error {}

const useFetchEventSource = () => {
  const [response, setResponse] = useState<any>({
    loading: false,
    genAIResponse: '',
    metadata: { attachments: [], title: '', description: '' },
    sourceRefsMetaData: [],
    genAIResponses: {},
    connected: false,
    isStreaming: false,
    error: '',
  });
  const navigate = useNavigate();
  const userAuthDetails = useAuthStore((state) => state, shallow);
  const streamRef = useRef<AbortController | null>(null);
  const { companyId } = useUserData();

  const fetchSSE = async (url: string, payload, isAttchements = false, attachmentId?: string) => {
    const browserName = getBrowserInfo();
    if (streamRef.current && !attachmentId) return;
    streamRef.current = new AbortController();

    if (attachmentId) {
      setResponse((prevState) => ({
        ...prevState,
        genAIResponses: {
          ...prevState.genAIResponses,
          [attachmentId]: {
            ...prevState?.genAIResponses?.[attachmentId],
            isLoading: true,
          },
        },
      }));
    } else {
      setResponse((prevState) => ({
        ...prevState,
        loading: true,
      }));
    }

    try {
      await fetchEventSource(url, {
        openWhenHidden: true,
        method: 'POST',
        headers: {
          ...(!isAttchements && { 'Content-Type': 'application/json' }),
          'Client-Id': API_CLIENT_ID,
          Authorization: `Bearer ${userAuthDetails?.accessToken || ''}`,
          'Company-Id': companyId,
        },
        body: isAttchements ? payload : JSON.stringify(payload),
        // ...(browserName === 'Chrome' && !attachmentId && { signal: streamRef.current.signal }),
        async onopen(response) {
          if (
            response.ok &&
            response.headers.get('content-type')?.includes(EventStreamContentType)
          ) {
            return; // everything's good
          } else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
            throw new FatalError();
          } else {
            throw new RetriableError();
          }
        },
        onmessage(msg) {
          if (msg.event === 'FatalError') {
            throw new FatalError(msg.data);
          }

          if (msg.data) {
            const parsedData = JSON.parse(msg.data);
            setResponse((prev) => {
              if (attachmentId) {
                return {
                  ...prev,
                  genAIResponses: {
                    ...prev.genAIResponses,
                    [attachmentId]: {
                      ...prev.genAIResponses[attachmentId],
                      documentData: parsedData?.metadata?.documentData || {},
                      gptPromptId: parsedData?.metadata?.gptPromptId || {},
                      llmResponse: `${prev?.genAIResponses[attachmentId]?.llmResponse || ''}${parsedData?.message || parsedData?.metadata?.llmResponse || ''}`,
                      isLoading: false,
                    },
                  },
                  loading: false,
                  isStreaming: !parsedData?.metadata,
                };
              } else {
                return {
                  ...prev,
                  genAIResponse: `${prev?.genAIResponse || ''}${parsedData?.message || ''}`,
                  loading: false,
                  isStreaming: !parsedData?.metadata,
                  ...(parsedData?.metadata && {
                    metadata: parsedData.metadata,
                    ...(Array.isArray(parsedData.metadata) && {
                      sourceRefsMetaData: parsedData.metadata,
                    }),
                  }),
                  ...(parsedData?.ERROR && { error: parsedData?.ERROR, isStreaming: false }),
                };
              }
            });
          }
        },
        onerror(error) {
          if (error?.status === 423) {
            const errorMsg = error?.response?.data?.errorPresent
              ? catchErrorMessage(error)
              : error?.message || 'User is deactivated. Please contact support team.';
            toast.error(errorMsg);
            userAuthDetails.resetState();
            navigate(LOGIN);
          } else {
            throw error; // rethrow to stop the operation
          }
        },
      });
    } catch (error) {
      console.warn(error);
      if (attachmentId) {
        setResponse((prevState) => ({
          ...prevState,
          isStreaming: false,
          genAIResponses: {
            ...prevState.genAIResponses,
            [attachmentId]: {
              ...prevState?.genAIResponses?.[attachmentId],
              isLoading: false,
            },
          },
          error,
        }));
      } else {
        setResponse((prevState) => ({
          ...prevState,
          isStreaming: false,
          loading: false,
          error,
        }));
      }
    }
    // finally {
    //   if (attachmentId) {
    //     setResponse((prevState) => ({
    //       ...prevState,
    //       genAIResponses: {
    //         ...prevState.genAIResponses,
    //         [attachmentId]: {
    //           ...prevState?.genAIResponses?.[attachmentId],
    //           isLoading: false,
    //         },
    //       },
    //     }));
    //   } else {
    //     setResponse((prevState) => ({
    //       ...prevState,
    //       loading: false,
    //     }));
    //   }
    // }
  };

  const fetchMultipleSSE = (url, payloadArray) => {
    payloadArray.forEach((payload) => {
      // Extract attachmentFileName from each payload object
      const { attachmentFileName } = payload;
      fetchSSE(url, payload, false, attachmentFileName);
    });
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.abort();
      streamRef.current = null;
    }
  };

  useEffect(() => {
    // aborting the stream if the component using this hook is unmounted
    return () => stopStream();
  }, [stopStream]);

  const reset = useCallback(() => {
    setResponse({
      loading: false,
      genAIResponse: '',
      metadata: { attachments: [], title: '', description: '' },
      sourceRefsMetaData: [],
      genAIResponses: {},
      connected: false,
      isStreaming: false,
      error: '',
    });
  }, []);
  return { fetchSSE, fetchMultipleSSE, setResponse, response, reset };
};

export default useFetchEventSource;
